import React from "react";
import { FileText, Zap } from "lucide-react";
import { DisconnectionRequest } from "../../../types";
import { useParams } from "react-router-dom";
import { useMeterHistory } from "@features/cx/disconnect/hooks";
import { Badge } from "@shared/ui/badge";
import { ValueLabel } from "@features/ax/shared/ValueLabel";

interface DisconnectionDetailsTabProps {
  request: DisconnectionRequest;
}

const cardClass = "border border-gray-200 p-4 rounded-md";

const DisconnectionDetailsTab: React.FC<DisconnectionDetailsTabProps> = ({
  request,
}) => {
  const { action } = useParams();
  const { data: meterHistory } = useMeterHistory(request.consumer?.id, action);
console.log("request", request?.priorityLevelDisplay);

  // Helper for NA
  const get = (val: any) =>
    val === undefined || val === null || val === "" ? "NA" : val;

  // Process meter data to create separate sections for each meter
  const meterSections = React.useMemo(() => {
    if (!request.consumer?.consumerMappingData) return [];

    return request.consumer.consumerMappingData.map((mapping, index) => {
      const meterDetails = mapping.meterDetails;
      return {
        meterIndex: index + 1,
        meterNumber: get(meterDetails?.meterNumber),
        productCode: get(meterDetails?.productCode),
        meterType: get(meterDetails?.meterTypeDisplay),
        currentReading: get(meterDetails?.lastReading),
        readingDate: get(meterDetails?.lastReadingDate),
        deviceNo: get(meterDetails?.deviceNo),
        status: get(meterDetails?.statusDisplay),
      };
    });
  }, [request.consumer?.consumerMappingData]);

  // SLA Data
  const slaTarget = {
    acknowledgementTime: get(
      request?.utilitySupportRequest?.extraData?.acknowledgementTime,
    ),
    responseTime: get(request?.utilitySupportRequest?.extraData?.responseTime),
    resolutionTime: get(
      request?.utilitySupportRequest?.extraData?.resolutionTime,
    ),
  };

  const slaAchieved = {
    acknowledgementTime: get(request?.acknowledgementTimeAchieved),
    responseTime: get(request?.acknowledgementTimeAchieved),
    resolutionTime: get(request?.resolutionTimeAchieved),
  };

  return (
    <div className="space-y-4">
      {/* Disconnection Details Card */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-50 rounded-lg">
            <FileText className="h-4 w-4 text-blue-600" />
          </div>
          <h2 className="font-bold text-base text-gray-900">
            Disconnection Details
          </h2>
        </div>
        <div className="space-y-4 p-4 border border-gray-200 rounded-md bg-white">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <ValueLabel
              label="Request ID"
              value={get(request.requestNo)}
              tooltip="The unique identifier assigned to this disconnection request."
            />
            <ValueLabel
              label="Reason"
              value={get(request.requestType)}
              tooltip="The specific reason provided for requesting a disconnection."
            />
            <ValueLabel
              label="Reason Code"
              value={get(request.utilitySupportRequest?.configurationCode)}
              tooltip="Internal system code for the disconnection category."
            />
            <ValueLabel
              label="Priority"
              value={get(request.priorityLevelDisplay)}
              tooltip="The urgency level assigned to this service request."
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <ValueLabel
              label="Preferred Date"
              value={get(request.requestDate)}
              tooltip="The specific date requested by the customer for the disconnection."
            />
            <ValueLabel
              label="Preferred Time"
              value={get(request.prefferedTimeSlotDisplay)}
              tooltip="The time window preferred by the customer for the service appointment."
            />
            <ValueLabel
              label="Required Approval"
              value={get(request.requiredApproval)}
              tooltip="Indicates if the disconnection request requires administrative approval."
            />
            <ValueLabel
              label="Service Address"
              value={get(request.serviceAddress)}
              tooltip="The physical address where the disconnection service will be performed."
            />
          </div>

          <div>
            <ValueLabel
              label="Description"
              value={get(request.utilitySupportRequest?.longDescription)}
              tooltip="Additional context and details regarding the disconnection request."
              className="text-gray-600 leading-relaxed max-w-4xl"
            />
          </div>

          <div>
            <ValueLabel
              label="Notes"
              value={get(request.consumerRemark)}
              tooltip="Internal remarks or customer-provided notes for the field team."
              className="text-gray-600"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md">
        {/* SLA Target */}
        <div className={cardClass}>
          <p className="card-heading-text mb-4">SLA Target</p>
          <div className="grid grid-cols-3 w-full gap-4">
            <ValueLabel
              label="Acknowledgement Time"
              value={slaTarget.acknowledgementTime}
            />
            <ValueLabel label="Response Time" value={slaTarget.responseTime} />
            <ValueLabel
              label="Resolution Time"
              value={slaTarget.resolutionTime}
            />
          </div>
        </div>

        {/* SLA Achieved */}
        <div className={cardClass}>
          <p className="card-heading-text mb-4">SLA Achieved</p>
          <div className="grid grid-cols-3 w-full gap-4">
            <ValueLabel
              label="Acknowledgement Time"
              value={slaAchieved.acknowledgementTime}
              className={
                slaAchieved.acknowledgementTime !== "Pending" &&
                slaAchieved.acknowledgementTime !== "NA"
                  ? "text-green-600"
                  : ""
              }
            />
            <ValueLabel
              label="Response Time"
              value={slaAchieved.responseTime}
              className={
                slaAchieved.responseTime !== "Pending" &&
                slaAchieved.responseTime !== "NA"
                  ? "text-blue-600"
                  : ""
              }
            />
            <ValueLabel
              label="Resolution Time"
              value={slaAchieved.resolutionTime}
              className={
                slaAchieved.resolutionTime !== "Pending" &&
                slaAchieved.resolutionTime !== "NA"
                  ? "text-green-600"
                  : ""
              }
            />
          </div>
        </div>
      </div>

      {/* Service Information / Allocation History */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-orange-50 rounded-lg">
              <Zap className="h-4 w-4 text-orange-400 fill-orange-400" />
            </div>
            <h2 className="font-bold text-base text-gray-900">
              {action === "current"
                ? "Service Information"
                : "Allocation History"}
            </h2>
          </div>
          <span className="text-[11px] font-medium text-gray-400">
            Water Meter
          </span>
        </div>

        {/* For Current Meter Info */}
        {action === "current" &&
          (meterSections.length > 0 ? (
            meterSections.map((meter, index) => (
              <div
                key={index}
                className="bg-[#fcfdfe] border border-gray-100 border-l-4 border-l-blue-600 rounded-lg p-5 relative transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-sm font-bold text-gray-900">
                    {meter.meterNumber}
                  </h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
                  <ValueLabel label="DEVICE NUMBER" value={meter.deviceNo} />
                  <ValueLabel label="METER TYPE" value={meter.meterType} />
                  <ValueLabel
                    label="UTILITY SERVICE"
                    value={meter.productCode}
                  />
                  <ValueLabel
                    label="CURRENT READING"
                    value={meter.currentReading}
                  />
                  <ValueLabel label="READING DATE" value={meter.readingDate} />
                  <ValueLabel
                    label="STATUS"
                    value= {meter.status}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center p-12 text-gray-400 bg-gray-50/30 rounded-xl border border-dashed border-gray-200">
              <Zap className="h-10 w-10 mb-3 opacity-20" />
              <p className="text-sm font-medium">No meter data available</p>
            </div>
          ))}

        {/* For Allocation History */}
        {action === "history" &&
          (meterHistory && meterHistory.length > 0 ? (
            meterHistory.map((meter, index) => (
              <div
                key={index}
                className="bg-[#fcfdfe] border border-gray-100 border-l-4 border-l-blue-600 rounded-lg p-5 relative transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-sm font-bold text-gray-900">
                    {meter.meterNumber}
                  </h3>
                  <Badge className="bg-blue-50 text-blue-600 hover:bg-blue-50 border-none px-4 py-1 rounded-full text-[10px] font-bold uppercase">
                    {meter.meterStatus}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
                  <ValueLabel
                    label="DEVICE NUMBER"
                    value={meter.deviceNumber}
                  />
                  <ValueLabel label="METER TYPE" value={meter.meterType} />
                  <ValueLabel
                    label="UTILITY SERVICE"
                    value={meter.utilityService}
                  />
                  {meter.meterStatus === "Unassigned" ? (
                    <ValueLabel
                      label="CURRENT READING"
                      value={meter.lastReading}
                    />
                  ) : (
                    <ValueLabel
                      label="FIRST READING"
                      value={meter.firstReading}
                    />
                  )}
                  {meter.meterStatus === "Unassigned" ? (
                    <ValueLabel
                      label="READING DATE"
                      value={meter.lastReadingDate}
                    />
                  ) : (
                    <ValueLabel
                      label="READING DATE"
                      value={meter.firstReadingDate}
                    />
                  )}
                  <ValueLabel
                    label="STATUS"
                    value={
                      <Badge className="bg-blue-50 text-blue-600 hover:bg-blue-50 border-none px-4 py-1 rounded-full text-[10px] font-bold uppercase mt-1">
                        {meter.meterStatus}
                      </Badge>
                    }
                  />
                  {meter.meterStatus === "Unassigned" ? (
                    <ValueLabel
                      label="UNASSIGNED DATE"
                      value={meter.disconnectionDate}
                    />
                  ) : (
                    <ValueLabel
                      label="ASSIGNED DATE"
                      value={meter.allocationDate}
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center p-12 text-gray-400 bg-gray-50/30 rounded-xl border border-dashed border-gray-200">
              <Zap className="h-10 w-10 mb-3 opacity-20" />
              <p className="text-sm font-medium">
                No assigned meter found in history
              </p>
            </div>
          ))}
      </div>
    </div>
  );
};

export default DisconnectionDetailsTab;
